// src/Dashboard/pages/admin/Attendance/components/AttendanceTablePanel.jsx
import React, { useMemo } from "react";
import { FaCrown, FaSyncAlt, FaTable } from "react-icons/fa";

import {
  daysArray,
  formatNumber,
  getAdherentName,
  getCellMeta,
  formatMonthLabel,
} from "../utils/attendanceUtils";

export default function AttendanceTablePanel({
  loading,
  selectedGroup,
  months = [],
  selectedMonth,
  setSelectedMonth,
  rows = [],
  kpis = {},
  highlightIds = [],
  onToggleCell,
  onRefresh,
  onChangeGroup,
}) {
  const days = useMemo(
    () => (selectedMonth ? daysArray(selectedMonth) : []),
    [selectedMonth]
  );

  const highlightSet = useMemo(
    () => new Set((highlightIds || []).map(Number)),
    [highlightIds]
  );

  return (
    <section className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-orange-500">
              Tableau mensuel
            </p>

            <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
              {selectedGroup?.nom || "Groupe"} ·{" "}
              {formatMonthLabel(selectedMonth)}
            </h2>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Cliquez une case pour basculer : non saisi → présent → absent →
              non saisi.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onChangeGroup}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950"
            >
              Changer de groupe
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <select
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            disabled={loading}
            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          >
            <option value="">Sélectionnez un mois</option>
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>

          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
            <LegendDot className="bg-emerald-500" label="Présent" />
            <LegendDot className="bg-rose-500" label="Absent" />
            <LegendDot className="bg-slate-300 dark:bg-slate-700" label="Non saisi" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi label="Adhérents" value={rows.length} />
        <Kpi label="Présences" value={kpis.presents || 0} tone="emerald" />
        <Kpi label="Absences" value={kpis.absents || 0} tone="rose" />
        <Kpi label="Taux" value={`${kpis.rate || 0}%`} tone="orange" />
        <Kpi label="Meilleure assiduité" value={kpis.bestCount || 0} tone="amber" />
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : !selectedMonth || rows.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-white text-slate-400 dark:bg-slate-900">
              <FaTable className="text-2xl" />
            </div>

            <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
              Aucun tableau à afficher
            </h3>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Sélectionnez un mois ou vérifiez que le groupe contient des
              adhérents.
            </p>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                <table className="w-full min-w-[1100px] border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-20 border-b border-r border-slate-200 bg-slate-50 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                        Adhérent
                      </th>

                      {days.map((day) => (
                        <th
                          key={day}
                          className="border-b border-r border-slate-200 bg-slate-50 px-1 py-3 text-center text-xs font-bold text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
                        >
                          {day}
                        </th>
                      ))}

                      <th className="border-b border-slate-200 bg-slate-50 px-3 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                        Total
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row) => {
                      const isTop = highlightSet.has(Number(row.id));

                      return (
                        <tr
                          key={row.id}
                          className="transition hover:bg-slate-50 dark:hover:bg-slate-950"
                        >
                          <td
                            className={`sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-900 ${
                              isTop ? "bg-amber-50 dark:bg-amber-900/20" : ""
                            }`}
                          >
                            <div className="flex min-w-[220px] items-center gap-2">
                              {isTop && (
                                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-amber-400 text-white">
                                  <FaCrown className="text-xs" />
                                </span>
                              )}

                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                                  {getAdherentName(row.adherent)}
                                </p>

                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {row.totalPresent} présent
                                  {row.totalPresent > 1 ? "s" : ""} ·{" "}
                                  {row.totalAbsent} absent
                                  {row.totalAbsent > 1 ? "s" : ""}
                                </p>
                              </div>
                            </div>
                          </td>

                          {days.map((day) => {
                            const value = row.dates?.[day];
                            const meta = getCellMeta(value);

                            return (
                              <td
                                key={day}
                                className="border-b border-r border-slate-200 px-1 py-2 text-center dark:border-slate-800"
                                title={meta.label}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    onToggleCell?.(row.id, day, value)
                                  }
                                  className={`mx-auto grid h-7 w-7 place-items-center rounded-lg text-[10px] font-bold ring-1 transition focus:outline-none focus:ring-2 focus:ring-orange-400 ${meta.className}`}
                                >
                                  {meta.short}
                                </button>
                              </td>
                            );
                          })}

                          <td className="border-b border-slate-200 px-3 py-2 text-center dark:border-slate-800">
                            <div className="text-sm font-bold text-slate-900 dark:text-white">
                              {row.totalPresent}
                            </div>

                            <div className="text-[11px] text-slate-500 dark:text-slate-400">
                              {row.rate}%
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-4 md:hidden">
              {rows.map((row) => {
                const isTop = highlightSet.has(Number(row.id));

                return (
                  <article
                    key={row.id}
                    className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
                  >
                    <div
                      className={`p-4 ${
                        isTop
                          ? "bg-amber-50 dark:bg-amber-900/20"
                          : "bg-slate-50 dark:bg-slate-900"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="truncate text-base font-bold text-slate-900 dark:text-white">
                            {getAdherentName(row.adherent)}
                          </h3>

                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {row.totalPresent} présent
                            {row.totalPresent > 1 ? "s" : ""} ·{" "}
                            {row.totalAbsent} absent
                            {row.totalAbsent > 1 ? "s" : ""} · {row.rate}%
                          </p>
                        </div>

                        {isTop && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-xs font-bold text-white">
                            <FaCrown />
                            Top
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-7 gap-2 p-4">
                      {days.map((day) => {
                        const value = row.dates?.[day];
                        const meta = getCellMeta(value);

                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => onToggleCell?.(row.id, day, value)}
                            className={`flex h-11 flex-col items-center justify-center rounded-xl text-[10px] font-bold ring-1 transition active:scale-95 ${meta.className}`}
                          >
                            <span>{day}</span>
                            <span className="mt-0.5 text-[10px]">
                              {meta.short || "—"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function LegendDot({ className, label }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-2 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-700">
      <span className={`h-3 w-3 rounded ${className}`} />
      {label}
    </span>
  );
}

function Kpi({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200",
    emerald:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200",
    rose: "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-200",
    orange:
      "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-200",
    amber:
      "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-200",
  };

  return (
    <article
      className={`rounded-3xl border border-slate-200 p-5 shadow-sm dark:border-slate-800 ${tones[tone]}`}
    >
      <p className="text-sm opacity-75">{label}</p>
      <p className="mt-2 text-2xl font-bold">{formatNumber(value)}</p>
    </article>
  );
}